import React, { useState, useEffect } from "react";
import SEO from "../seo";
const SetUpPolicyPages = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "create-policy-pages", "tips"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Set Up Policy Pages – xPAGE Help Center"
        description="Learn how to create and manage essential policy pages for your xPage store, including Privacy Policy, Return & Refund Policy, and Terms of Service."
        keywords="xPAGE, policy pages, privacy policy, return policy, terms of service, online store compliance, eCommerce"
        canonical="https://help.xpage.ai/help/landing-page-compliance/Set%20Up%20Policy%20Pages"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Set Up Policy Pages
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          Policy pages help protect your business, inform customers of their
          rights, and ensure compliance with laws, regulations, and payment
          provider requirements. In xPage, you can create and manage essential
          policy pages such as Privacy Policy, Return & Refund Policy, and Terms
          of Service directly from your store settings.
        </p>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              With xPage, you can:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Create and edit your legal policy pages.</li>
              <li>
                <strong>Automatically display policies</strong> in your landing
                page <strong>header and footer</strong>, as well as during
                checkout.
              </li>
              <li>
                Customize content to match your business requirements or
                regional regulations.
              </li>
            </ul>
          </div>
        </section>

        {/* Create Policy Pages Section */}
        <section id="create-policy-pages" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Create Your Policy Pages
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the <strong>xPage Admin</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to <strong>Settings</strong> →{" "}
                  <strong>Policies</strong>
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the policy you want to create:
                </span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>Privacy Policy</li>
                  <li>Return & Refund Policy</li>
                  <li>Terms of Service</li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter your policy text in the editor
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click <strong>Save</strong>
                </span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Note:</span> Policies are
                automatically linked in your landing page{" "}
                <strong>header and footer</strong>, so there's no need to
                manually add them to menus.
              </p>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section id="tips" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Tips for Policies
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Keep your policies{" "}
                <strong>clear, concise, and customer-friendly</strong>.
              </li>
              <li>
                Review policies periodically to ensure{" "}
                <strong>
                  compliance with local laws and payment provider requirements.
                </strong>
              </li>
              <li>
                Since policies are automatically displayed in the landing page{" "}
                <strong>header, footer, and checkout,</strong> your customers
                can access them easily from anywhere.
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#create-policy-pages"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "create-policy-pages"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Create Policy Pages
              </a>
            </li>
            <li>
              <a
                href="#tips"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "tips"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Tips for Policies
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetUpPolicyPages;
